import React, { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import noop from 'lodash/noop';
import { getLanguageCode } from 'modules/meta/selectors';
import { toggle } from 'opticks';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { NoStyleLink } from '@daedalus/atlas/helpers/NoStyleLink';
import { Text } from '@daedalus/atlas/Text';
import { useIsCCPAState, useIsGDPRCountry } from '@daedalus/core/src/_web/utils/cookies/hooks/cookieComplianceGeolocation';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { TrackEventPayload } from '@daedalus/core/src/analytics/types/Events';
import { Brand } from '@daedalus/core/src/brand/types';
import { checkIsCookieConsentEnabledForBrand, getLocalizedContactUrl, getLocalizedHelpUrl } from '@daedalus/core/src/brand/utils';
import { useCookieConsent } from '@daedalus/shared/src/search/cookies/useCookieConsent';
const messages = defineMessages({
  productTitle: {
    id: 'product',
    defaultMessage: 'Product'
  },
  companyTitle: {
    id: 'company',
    defaultMessage: 'Company'
  },
  contactTitle: {
    id: 'contact',
    defaultMessage: 'Contact'
  },
  affiliateProgram: {
    id: 'affiliateProgram',
    defaultMessage: 'Affiliate program'
  },
  cookies: {
    id: 'cookies',
    defaultMessage: 'Cookies'
  },
  privacy: {
    id: 'privacy',
    defaultMessage: 'Privacy'
  },
  terms: {
    id: 'terms',
    defaultMessage: 'Terms'
  },
  aboutUs: {
    id: 'aboutUs',
    defaultMessage: 'About us'
  },
  blog: {
    id: 'blog',
    defaultMessage: 'Blog'
  },
  careers: {
    id: 'careers',
    defaultMessage: 'Careers'
  },
  getHelp: {
    id: 'getHelp',
    defaultMessage: 'Get help'
  },
  partnerships: {
    id: 'partnerships',
    defaultMessage: 'Partnerships'
  },
  contactUs: {
    id: 'contactUs',
    defaultMessage: 'Contact us'
  }
});
interface LinksSectionProps {
  brand: Brand;
}
export const FooterLinksSection = ({
  brand
}: LinksSectionProps) => {
  const cookieConsentIsEnabled = checkIsCookieConsentEnabledForBrand(brand);
  const {
    formatMessage
  } = useIntl();
  const languageCode = useSelector(getLanguageCode);
  const isGDPRCountry = useIsGDPRCountry();
  const isCCPAState = useIsCCPAState();
  const isNonGDPRCookieBannerEnabled = !isGDPRCountry && toggle('0f0v1ebh-cookie-banner-on-non-gdpr-countries', false, true);
  const showCookiePreferencesLink = (isGDPRCountry || isCCPAState || isNonGDPRCookieBannerEnabled) && cookieConsentIsEnabled;
  const dispatchTeamEvent = useDispatchTeamEvent();
  const trackEventCallback = useCallback((payload: TrackEventPayload) => dispatchTeamEvent(trackEvent(payload)), [dispatchTeamEvent]);
  const {
    openPreferencesCenter
  } = useCookieConsent(noop, trackEventCallback);
  return <>
      <LinkSection title={formatMessage(messages.productTitle)} links={[{
      label: formatMessage(messages.affiliateProgram),
      href: brand.links.developersUrl
    }, showCookiePreferencesLink && {
      label: formatMessage(messages.cookies),
      onClick: openPreferencesCenter
    }, {
      label: formatMessage(messages.privacy),
      href: brand.links.privacyPolicyUrl
    }, {
      label: formatMessage(messages.terms),
      href: brand.links.termsOfUseUrl
    }]} />
      <LinkSection title={formatMessage(messages.companyTitle)} links={[{
      label: formatMessage(messages.aboutUs),
      href: brand.links.aboutUsUrl
    }, {
      label: formatMessage(messages.blog),
      href: brand.links.blogUrl
    }, {
      label: formatMessage(messages.careers),
      href: brand.links.careersUrl
    }]} />
      <LinkSection title={formatMessage(messages.contactTitle)} links={[{
      label: formatMessage(messages.getHelp),
      href: getLocalizedHelpUrl(brand, languageCode)
    }, {
      label: formatMessage(messages.partnerships),
      href: brand.links.partnershipsUrl
    }, {
      label: formatMessage(messages.contactUs),
      href: getLocalizedContactUrl(brand, languageCode)
    }]} />
    </>;
};
interface LinkSectionProps {
  title: string;
  links: Array<{
    label: string;
    href?: string;
    onClick?: () => void;
  }>;
}
export const LinkSection = ({
  title,
  links
}: LinkSectionProps) => {
  const validLinks = useMemo(() => links.filter(link => link && (link.href || link.onClick)), [links]);
  if (!validLinks.length) {
    return null;
  }
  return <div>
      <Text variant="titleL">{title}</Text>
      <ContentWrapper paddingTop="s400">
        <UnstyledList>
          {validLinks.map(({
          label,
          href,
          onClick
        }) => <li key={label}>
              {href ? <StyledLink target="_blank" href={href}>
                  {label}
                </StyledLink> : <StyledLink onClick={onClick}>{label}</StyledLink>}
            </li>)}
        </UnstyledList>
      </ContentWrapper>
    </div>;
};
const UnstyledList = /*#__PURE__*/styled('ul')({
  name: "UnstyledList",
  class: "u83sb8f",
  propsAsIs: false
});
const _exp2 = /*#__PURE__*/() => NoStyleLink;
const StyledLink = /*#__PURE__*/styled(_exp2())({
  name: "StyledLink",
  class: "smxa84v",
  propsAsIs: true
});

require("./LinksSection.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./LinksSection.tsx");